/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Configure the plan and pay for it, on one screen.
 *
 * Two cards: what the buyer fills in, and what they owe. The card fields
 * belong to the first one — they are typing, and they sit where the rest of
 * the typing is — which means the payment frame is part of the form rather
 * than a panel that appears at the end once everything else is right.
 *
 * ── Where the pay button lives ────────────────────────────────────────────
 * Inside the frame, with the card fields, and not beside the total where the
 * handoff drew it. Charging a card has to follow a click inside Payien's own
 * document: a button on this page posting "submit" into the frame turns any
 * script that reaches this origin — the GTM container included — into a
 * charge with no human behind it.
 *
 * ── Clicking before the host is ready ─────────────────────────────────────
 * The button is always live, so the click can arrive while the agency's own
 * fields are still empty. The frame says so (`payien_needs_session`), the
 * host checks its own fields, marks the ones that are wrong, and arms the
 * frame only when there is something real to charge against.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton, useStepsContext } from '@/shared/ui';
import LogoRingSpinner from '@/shared/ui/components/basic/loading/LogoRingSpinner';
import type { PlanSegment } from '@/features/pricing';
import { isPlanSelectable, usePlans } from '../../hooks/use-plans';
import type { BillingCycle } from '../../hooks/use-plans';
import { clearPersistedPaymentAttempt } from '../../hooks/usePersistedOnboarding';
import { useTrialEligibility } from '../../hooks/use-trial-eligibility';
import { useStoreSetters } from '@/shared/state';
import { useAuth } from '@/context/authentication';
import { AgenciesService } from '@/services/requests/housien-services/AgenciesService';
import {
  resolveAgencyId, createCheckoutUrl, reusableAgencyId, CheckoutCouponError,
} from './Payment.checkout';
import {
  asApplied, couponErrorKey, couponFingerprint, fromSession, type AppliedCoupon,
} from './Checkout.coupon';
import { CouponField } from './Checkout.couponField';
import { CheckoutIssuesDialog, type CheckoutIssues } from './Checkout.issuesDialog';
import { paymentErrorMessage } from './Checkout.paymentError';
import { highlightPlan, highlightRows } from './Checkout.highlights';
import { buildQuotaLabels } from './PlanChoice.catalog';
import CheckoutIframe, { type CheckoutIframeHandle } from './payment/CheckoutIframe';
import { useFieldsFrame } from './payment/checkout-fields';
import MockPaymentConfirmation from './payment/MockPaymentConfirmation';
import { FF_MOCK_PAYMENTS } from '@/shared/utils/feature-flag';
import {
  CheckoutHeader, CheckoutSummary, CycleRow, FormCard,
  PayerFields, SeatCards, SeatFloorNotice, StartTrialButton, type TrialMode,
} from './Checkout.parts';
import {
  CEP_DIGITS, REQUIRED_FIELDS, agencyPayload, checkoutLines, fieldIssueKey, missingFields, monthlyTotal,
  planTrialDays, priceBreakdown, shownTrialDays, totalSeats, SUBSCRIPTION_SEAT_FLOOR,
  seatRange, sessionKey, summaryLines, supportSeats, trialDaysFor, type CheckoutForm, type RequiredField,
  type SeatsByPlan,
} from './Checkout.pricing';
import { useZipCode } from '../../hooks/use-zip-code';
import './checkout-fields.css';

/**
 * How long the form must be still before a session is opened.
 *
 * Sessions are single-use and price-bearing, so one per keystroke would burn
 * through them while the customer is still typing their address.
 */
const SETTLE_MS = 600;

/**
 * The longest the screen stays covered waiting for the frame to draw itself.
 *
 * A frame that never reports a height — blocked, broken, served by something
 * that is not Payien — would otherwise hold the cover forever, and a page
 * that never opens is worse than a block that appears late. Past this, the
 * screen opens regardless and the frame lands in the space kept for it.
 */
const FRAME_DRAW_DEADLINE_MS = 12000;

/** How long the confirmation is shown before the activation step takes over. */
const CONFIRMATION_MS = 1200;

/**
 * The longest the page may stay covered while a click is being answered.
 *
 * Matched to the frame's own deadline: it gives its button back thirty
 * seconds after a `payien_needs_session` that was never armed, and a cover
 * that outlived that would leave a live button under a dead curtain.
 */
const ARMING_DEADLINE_MS = 30000;

/**
 * The frame's field names, as the issue each one stands for.
 *
 * The frame says which of its fields are wrong and never what was typed into
 * them, so the sentence is the host's: one per field, in the dialog.
 */
const FRAME_ISSUE_KEYS: Record<string, string> = {
  number: 'cardNumber',
  name: 'cardName',
  expiry: 'cardExpiry',
  cvv: 'cardCvv',
  tax_id: 'documentInvalid',
  email: 'email',
};

/**
 * The size of the fields this page owns.
 *
 * Set here, on the one element that wraps both bands, because the fields
 * *inside* the payment frame are drawn at 16px: below that iOS zooms the
 * viewport on focus, and the viewport it zooms is the host's — so the frame
 * cannot come down to meet a smaller host, and the host goes up instead.
 *
 * The two bands are one form with a cross-origin seam running through it, and
 * a buyer reading down it must not be able to see where the seam is. 16px and
 * 44px on both sides is what closes it; the ground, the border and the corner
 * (`--surface-primary`, `--border-subtle`, `--radius-md`) are already the
 * handoff's tokens here, and the frame was moved onto the same three.
 */
const HOST_FIELD_TOKENS = {
  '--checkout-field-text': 'var(--text-body-lg)',
  '--checkout-field-height': '44px',
} as React.CSSProperties;

const EMPTY_FORM: CheckoutForm = {
  name: '', document: '', phone: '', cep: '', number: '',
};

/** The address fields, which a card-less trial does not ask for. */
const ADDRESS_FIELDS: RequiredField[] = ['cep', 'number'];

/**
 * The shape of trial this visit is being shown, when the URL forces one.
 *
 * The catalogue decides whether there is a trial at all; this only exists so
 * the card-less variant can still be put on screen and judged, and so a
 * reviewer can see the paid screen while every plan is offering free days.
 * `trial=off` is the one that takes the offer away.
 *
 * @returns The forced mode, `'off'` to suppress, or null to follow the catalogue.
 */
function trialFromUrl(): TrialMode | 'off' {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('trial');
  if (value === 'off') return 'off';
  return value === 'card' || value === 'nocard' ? value : null;
}

/**
 * The day a trial starts billing, written the way the summary says it.
 *
 * @param language - The active locale.
 * @param days - How many free days the catalogue grants.
 * @returns The date, as "19 de out".
 */
function trialDate(language: string, days: number): string {
  const day = new Date();
  day.setDate(day.getDate() + days);
  return day.toLocaleDateString(language, { day: 'numeric', month: 'short' })
    .replace('.', '');
}

/**
 * The session id a checkout URL carries.
 *
 * @param url - The URL Payien answered with.
 * @returns The session id, or null when the URL does not name one.
 */
function sessionIdOf(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get('session_id');
  } catch {
    return null;
  }
}

/**
 * A name made out of an email address, when the claims carry none.
 *
 * The same derivation the mock provider uses, so the two modes behave
 * alike: the local part, split on its separators and capitalised.
 *
 * @param email - The address on the account.
 * @returns A name, or an empty string when there is no address either.
 */
function nameFromEmail(email: unknown): string {
  if (typeof email !== 'string' || !email.includes('@')) return '';
  return email.split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

/**
 * The account holder's name, as the SSO claims give it.
 *
 * ── Why the account name is only a starting value ────────────────────────
 * A solo broker's razão social is the office name, not the person who signed
 * in. The account name prefills the field so the invoice is never blank, and
 * they can replace it with the name the office actually uses.
 *
 * ── When the claim does not come ──────────────────────────────────────────
 * This name is not decoration. It travels into `POST /agencies` as the
 * organisation's name, from there into the Payien session as
 * `organization_name`, and from there onto the nota fiscal — so an empty one
 * would create an agency that cannot be invoiced. Google and K4D SSO both
 * send `name`, but "usually" is not a guarantee, so there are two fallbacks:
 * the email's local part, and — if even that is missing — the office-name
 * field stays empty until they type one, because asking is better than inventing.
 *
 * @param profile - The claims on the signed-in user.
 * @returns The name, or an empty string when nothing resolves one.
 */
export function resolveAccountName(profile?: Record<string, unknown>): string {
  const fromClaims = [
    profile?.full_name,
    profile?.name,
    [profile?.given_name, profile?.family_name].filter(Boolean).join(' '),
  ]
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .find((value) => value.length > 0);
  return fromClaims || nameFromEmail(profile?.email);
}

interface CheckoutProps {
  planId: string;
  planName: string;
  planKey: string;
  billingFrequency: BillingCycle;
  /** Monthly-equivalent price of one seat, as the plan grid showed it. */
  seatPrice: number;
  segment: PlanSegment;
  agencyId?: string;
  /** The payer the saved `agencyId` was created for. See OnboardingValues. */
  agencyPayerKey?: string | null;
  agencyName?: string;
  /**
   * What the payer's details already are, for an agency that exists.
   *
   * Reactivating or activating a draft used to open on five empty fields the
   * agency had already filled when it was created, and retyping a CNPJ to buy
   * the plan for the agency that CNPJ belongs to is a question with only one
   * acceptable answer.
   */
  initialForm?: Partial<CheckoutForm>;
  /**
   * The payer is this agency's own record and is not editable here.
   *
   * Set by the in-product subscribe flow: the agency already exists, so its
   * details are shown and not asked for, and the checkout charges that
   * agency instead of resolving a payer out of the form.
   */
  payerFixed?: boolean;
  /**
   * Seats to start from, per plan key.
   *
   * Reactivation comes back to a team that already had a size: opening the
   * counters at the floor would ask an agency of six to count itself again,
   * and silently quote for two.
   */
  initialSeats?: SeatsByPlan;
}

const Checkout: React.FC<CheckoutProps> = ({
  planKey, segment, agencyId, agencyPayerKey, agencyName, initialForm,
  payerFixed = false, initialSeats,
}) => {
  const { t, i18n } = useTranslation('@pages/onboarding');
  const { setCurrentStepId } = useStepsContext();
  const { updateValues } = useStoreSetters();

  const { plans, billingCycle, setBillingCycle } = usePlans({ defaultSegment: segment });
  const [tierKey] = useState(planKey);
  const [form, setForm] = useState<CheckoutForm>({
    ...EMPTY_FORM, name: agencyName ?? '', ...initialForm,
  });

  /*
   * The payer details are fetched, so they land after the first render and
   * the initial state above misses them. Without this the boxes stayed empty
   * while `lockedFields` already held them shut, which is a checkout nobody
   * can finish. Only empty boxes are filled: whatever the buyer typed wins.
   */
  useEffect(() => {
    if (!initialForm) return;
    setForm((current) => {
      const filled = REQUIRED_FIELDS.filter((field) => initialForm[field] && !current[field]);
      if (filled.length === 0) return current;
      const next = { ...current };
      filled.forEach((field) => { next[field] = initialForm[field] as string; });
      return next;
    });
  }, [initialForm]);

  // The address is not typed: the CEP resolves it, and Payien needs the
  // street, the municipality and its IBGE code to issue the invoice.
  const zip = useZipCode(form.cep);

  useEffect(() => {
    if (
      form.cep.replace(/\D/g, '').length === CEP_DIGITS
      && zip.status === 'idle'
      && !zip.address
    ) {
      zip.lookup(form.cep);
    }
  }, [form.cep, zip]);

  const [forcedTrial] = useState<TrialMode | 'off'>(trialFromUrl);
  const agency = segment === 'agency';

  // A solo broker's office name starts as the account name, which is the only
  // one we know before they type. Once they edit it, that edit is the name
  // the agency is created with — overwriting it put their personal name on
  // the razão social and left no way to correct it on this screen.
  const auth = useAuth();
  const accountName = resolveAccountName(
    auth.user?.profile as Record<string, unknown> | undefined,
  );
  useEffect(() => {
    if (agency || !accountName) return;
    setForm((current) => (
      current.name.trim() ? current : { ...current, name: accountName }
    ));
  }, [agency, accountName]);
  /* Only what the record actually carries: locking an empty box would leave
     the buyer unable to supply the one field standing between them and the
     checkout. */
  const lockedFields = useMemo(
    () => (payerFixed ? REQUIRED_FIELDS.filter((field) => Boolean(initialForm?.[field])) : []),
    [payerFixed, initialForm],
  );

  const plan = plans.find((p) => p.key === tierKey) ?? plans[0];

  // An agency builds a team, and the team may mix tiers: every plan gets its
  // own counter, and the ones left at zero are plans it does not use.
  const [seatsBy, setSeatsBy] = useState<SeatsByPlan>(initialSeats ?? {});
  // Everything downstream of the seat mix is built from this list rather than
  // from `plans`, so an announced tier cannot be counted, priced or charged.
  const sellable = useMemo(() => plans.filter(isPlanSelectable), [plans]);
  useEffect(() => {
    setSeatsBy((current: SeatsByPlan) => {
      if (!agency) return { [plan?.key ?? '']: 1 };
      // Only seats of plans this segment actually sells: a seat left under a
      // plan that is not on offer counted towards the total and priced at
      // nothing, and the "add at least 2" warning went away one seat early.
      const onOffer = Object.fromEntries(
        Object.entries(current).filter(([key]) => sellable.some((p) => p.key === key)),
      );
      if (Object.keys(onOffer).length) return onOffer;
      const start = sellable.find((p) => p.recommended) ?? sellable[0];
      return start ? { [start.key]: SUBSCRIPTION_SEAT_FLOOR } : {};
    });
  }, [agency, plan?.key, sellable]);

  const lines = checkoutLines(sellable, seatsBy, billingCycle);
  const seats = totalSeats(seatsBy);
  const enoughSeats = !agency || seats >= SUBSCRIPTION_SEAT_FLOOR;

  const isYearly = billingCycle === 'yearly';
  // The monthly rate for one seat, before the yearly discount: the
  // catalogue's yearly price already has the two free months applied, so
  // using it as the base takes them off a second time.
  const seatPriceNow = plan?.monthlyPrice ?? plan?.price ?? 0;

  const price = priceBreakdown({
    seatPrice: agency ? monthlyTotal(sellable, seatsBy) : seatPriceNow,
    seats: agency ? 1 : seats,
    cycle: billingCycle,
  });
  // The offer is the catalogue's, not the screen's: Payien reads the same
  // `trial_days` off its own copy and bills from it, so anything decided
  // here would be a promise the charge does not keep. A team buying a mix
  // gets the shortest trial of the lines it chose, which is the `min()`
  // Payien applies.
  // The catalogue offers it, but only an owner the backend confirmed as
  // eligible is promised it (RN-05, AR-07): until that is known, or if the
  // lookup failed, the screen shows what is charged today.
  const trialStatus = useTrialEligibility(payerFixed ? agencyId : undefined);
  const trialDays = shownTrialDays(trialStatus, agency
    ? trialDaysFor(sellable, seatsBy, billingCycle)
    : planTrialDays(plan, billingCycle));
  const trial: TrialMode = forcedTrial === 'off' || trialDays === 0
    ? null
    : forcedTrial ?? 'card';

  const quotaLabels = useMemo(() => buildQuotaLabels(plans), [plans]);
  const highlights = useMemo(
    () => highlightRows(
      highlightPlan(plans, agency ? seatsBy : { [tierKey]: seats }, tierKey),
      quotaLabels,
      i18n.language,
    ),
    [plans, seatsBy, tierKey, seats, agency, quotaLabels, i18n.language],
  );

  // A card-less trial never reaches an invoice, so it never asks for the
  // address that would go on one.
  const withAddress = trial !== 'nocard';
  const missing = missingFields(form, segment, zip.address)
    .filter((field) => withAddress || !ADDRESS_FIELDS.includes(field));
  const complete = missing.length === 0 && enoughSeats;

  const showFieldIssuesRef = useRef<(fromFrame: string[]) => void>(() => {});
  /**
   * Opens the dialog naming every field that stands between the buyer and
   * paying, the frame's and this page's alike, each with what is wrong.
   *
   * @param fromFrame - The frame's invalid field names.
   */
  showFieldIssuesRef.current = (fromFrame: string[]) => {
    const items = [
      ...(enoughSeats ? [] : [t('checkoutStep.details.seatsTooFew', { min: SUBSCRIPTION_SEAT_FLOOR })]),
      ...missing.map((field) => t(`checkoutStep.issues.${fieldIssueKey(field, form, zip.status)}`)),
      ...fromFrame.map((field) => t(`checkoutStep.issues.${FRAME_ISSUE_KEYS[field] ?? 'cardOther'}`)),
    ];
    if (items.length) setIssues({ title: t('checkoutStep.issues.fieldsTitle'), items });
  };

  // ── Who owns the discount ──────────────────────────────────────────────
  // The open checkout session does. Payien's apply-coupon revalidates the
  // code against the session it already holds and persists
  // `final_amount_cents` on it, and the charge is taken from that number.
  const [sessionCoupon, setSessionCoupon] = useState<AppliedCoupon | null>(null);
  // The old route, kept for a Payien that does not answer the apply-coupon
  // message: the host checks the code itself and passes it into a *new*
  // session, which costs the buyer their card a second time.
  const [rebuiltCoupon, setRebuiltCoupon] = useState<AppliedCoupon | null>(null);
  const coupon = sessionCoupon ?? rebuiltCoupon;
  // The code the buyer applied, kept until they remove it. See the
  // re-application effects below.
  const [heldCode, setHeldCode] = useState<string | null>(null);
  const heldCodeRef = useRef<string | null>(null);
  heldCodeRef.current = heldCode;
  const [reapply, setReapply] = useState<{ code: string; seq: number } | null>(null);
  const reapplySeq = useRef(0);
  const [issues, setIssues] = useState<CheckoutIssues | null>(null);
  const framed = useRef<CheckoutIframeHandle>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  // Raised by the frame while the card is being charged.
  //
  // The frame draws its own overlay inside its ~400px, and that overlay is
  // the only place the buyer ever reads the issuer's refusal. This cover
  // sits on top of it, so it has to come down the moment the charge stops:
  // the frame says `payien_state: 'idle'` when it does, and that is what
  // clears this. The deadline below is the backstop for a frame that goes
  // quiet, so a refusal can never end up unreadable under a curtain.
  const [charging, setCharging] = useState(false);
  // Raised by the host between a pay click that found no session and the
  // arming message that answers it. The frame does not report this stretch
  // — it only changes its own button — so the cover is the host's to draw.
  const [arming, setArming] = useState(false);
  const [frameDrawn, setFrameDrawn] = useState(false);
  // The deadline passed and the frame never said it had drawn itself: the
  // cover lifts, and the band says so instead of standing empty.
  const [frameStalled, setFrameStalled] = useState(false);
  const [frameReloads, setFrameReloads] = useState(0);
  const [paid, setPaid] = useState(false);
  // True once the buyer has pressed pay: until then nothing is drawn in red,
  // because a form that marks fields before they have been filled is just a
  // page of errors.
  const [tried, setTried] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  // Fields the frame said are not fillable, counted into the note under the
  // total. Names only — the frame never sends what was typed into them.
  const [frameInvalid, setFrameInvalid] = useState<string[]>([]);
  // Bumped by "tentar de novo": the fingerprint has not changed, so without
  // it the effect would have nothing to react to.
  const [retryTick, setRetryTick] = useState(0);
  // Set when a click arrived before a session existed, cleared once the
  // session that answers it has been handed over.
  const armWanted = useRef(false);
  const agencyPayerKeyRef = useRef<string | null>(null);

  // The resolved address is part of the fingerprint: a CEP that re-resolves
  // to a different municipality changes the invoice. The seat map is too:
  // moving a seat between tiers can leave the total untouched while changing
  // what is being sold.
  const baseKey = sessionKey({
    planId: JSON.stringify(lines), cycle: billingCycle, seats, form,
  })
    + `|${zip.address?.codigo_municipio ?? ''}`;
  // Only the fallback coupon is part of the fingerprint. One applied to the
  // open session reached the price without this session being built again.
  const key = baseKey + couponFingerprint(rebuiltCoupon);

  const sessionCouponRef = useRef<AppliedCoupon | null>(null);
  sessionCouponRef.current = sessionCoupon;
  const rebuiltCouponRef = useRef<AppliedCoupon | null>(null);
  rebuiltCouponRef.current = rebuiltCoupon;

  /**
   * Takes the discount off and asks for it again against the current price.
   *
   * The discount was a number of reais off a total that no longer exists, so
   * it cannot simply stay; but the code is the buyer's and does. Dropping it
   * and asking for it to be typed again read as the screen losing it.
   */
  const requestReapply = useCallback((code: string) => {
    setSessionCoupon(null);
    setRebuiltCoupon(null);
    reapplySeq.current += 1;
    setReapply({ code, seq: reapplySeq.current });
  }, []);

  // A change to *what is being bought*: the code goes to the new total.
  const cartFingerprint = `${JSON.stringify(lines)}|${billingCycle}|${seats}`;
  useEffect(() => {
    if (heldCodeRef.current) requestReapply(heldCodeRef.current);
  }, [cartFingerprint, requestReapply]);

  // A replacement session has never heard of a code applied to the old one.
  // One rebuilt *around* the code already carries it, and re-applying that
  // one would rebuild it again, forever.
  useEffect(() => {
    if (heldCodeRef.current && sessionCouponRef.current) requestReapply(heldCodeRef.current);
  }, [checkoutUrl, requestReapply]);

  /**
   * Opens the session the frame will charge against.
   *
   * @returns The checkout URL, or null when there is nothing to open one for.
   */
  const openSession = useCallback(async (): Promise<string | null> => {
    // The payer the form currently describes. `reusableAgencyId` compares it
    // against the key recorded when `agencyId` was created: an identical
    // payer is a plain retry of this attempt and keeps the one draft (no
    // second agency), while a changed payer -- a different document, phone,
    // or address NUMBER -- means the saved draft is for a different office
    // and a fresh agency must be resolved (BUG-011). The ref holds the
    // recorded key for this tab's lifetime; the persisted value holds it
    // across a reload. A genuinely new attempt clears both via
    // `clearPersistedOnboarding` before "+ Novo escritório" starts over.
    const payerKey = [
      segment, form.name, form.document, form.phone,
      form.cep, form.number, zip.address?.codigo_municipio ?? '',
    ].join('|');
    const knownPayerKey = agencyPayerKeyRef.current ?? agencyPayerKey ?? null;
    /*
     * A fixed payer is the agency in the URL, full stop. Running it through
     * `reusableAgencyId` drops it — the key it compares against only exists
     * for a draft this tab created — and the checkout would then ask the API
     * to create an agency that is already there, recovering only by reading
     * the id back out of an "already exists" error.
     */
    const candidateAgencyId = payerFixed
      ? agencyId ?? null
      : reusableAgencyId(agencyId, knownPayerKey, payerKey);
    const payload = agencyPayload(form, zip.address, segment);
    const agencyType = segment === 'solo' ? 'solo_broker' : 'agency';
    const rememberAgency = (id: string) => {
      agencyPayerKeyRef.current = payerKey;
      updateValues({ agencyId: id, agencyPayerKey: payerKey });
    };

    const resolvedAgency = await resolveAgencyId({
      agencyId: candidateAgencyId,
      formData: payload,
      type: agencyType,
      onAgencyCreated: rememberAgency,
      onAgencyReused: rememberAgency,
    } as any);
    // With payments mocked there is no session to open, and asking for one
    // would hand the iframe a URL on a host that is not running.
    if (!resolvedAgency || FF_MOCK_PAYMENTS || !plan) return null;
    const planIdForCycle = (isYearly ? plan.yearlyId : plan.monthlyId)
      || plan.originalId || plan.key;
    const lineItems = lines.map((l) => ({ plan_id: l.planId, seats: l.seats }));
    try {
      return await createCheckoutUrl(
        planIdForCycle, resolvedAgency, seats, lineItems, coupon?.code,
      ) ?? null;
    } catch (err) {
      // A refused coupon is not a stale-agency problem. Rebuilding would get
      // the same refusal and leave a second agency behind (BUG-025).
      if (err instanceof CheckoutCouponError) throw err;
      if (!candidateAgencyId) throw err;
      const freshAgency = await resolveAgencyId({
        agencyId: null,
        formData: payload,
        type: agencyType,
        onAgencyCreated: rememberAgency,
        onAgencyReused: rememberAgency,
      } as any);
      if (!freshAgency) throw err;
      return await createCheckoutUrl(
        planIdForCycle, freshAgency, seats, lineItems, coupon?.code,
      ) ?? null;
    }
  }, [agencyId, agencyPayerKey, payerFixed, form, zip.address, segment, plan, isYearly, seats, lines, coupon?.code, updateValues]);

  // The session still opens on a settled form rather than on the click: the
  // agency record behind it is created here too, and moving that to the
  // click would put a round trip the buyer can watch between pressing pay
  // and anything happening.
  useEffect(() => {
    // Nothing opens before the offer is known: a session opened for the
    // wrong amount would be thrown away the moment the answer came.
    if (!complete || !plan || trial === 'nocard' || trialStatus === 'loading') {
      setCheckoutUrl(null);
      return;
    }
    setSessionError(null);
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const url = await openSession();
        if (!cancelled && url) setCheckoutUrl(url);
      } catch (err) {
        if (!cancelled) {
          setSessionError(err instanceof CheckoutCouponError
            ? err.errorCode
            : (err instanceof Error ? err.message : 'unknown'));
          if (armWanted.current) {
            armWanted.current = false;
            framed.current?.refuseArm();
            setArming(false);
          }
        }
      }
    }, SETTLE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // `openSession` closes over everything `key` already fingerprints, so
    // listing it here would re-open a session on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, complete, retryTick, trial, trialStatus]);

  const sessionId = sessionIdOf(checkoutUrl);

  // A session that would not open is said in the dialog too, with the same
  // retry the payment band offers.
  useEffect(() => {
    if (!sessionError) return;
    setIssues({
      title: t('checkoutStep.issues.paymentTitle'),
      items: [sessionError.startsWith('coupon_') ? t(couponErrorKey(sessionError)) : t('checkoutStep.payment.failed')],
      retry: { label: t('checkoutStep.payment.retry'), onClick: () => setRetryTick((n) => n + 1) },
    });
  }, [sessionError, t]);

  // ── What the frame is pointed at ───────────────────────────────────────
  // The sessionless fields frame, when Payien will serve one. It is born at
  // the first paint, before an agency exists and before a session could be
  // opened, and it keeps the card the buyer typed through every change to
  // the cart — because its `src` never changes. The session reaches it by
  // `arm`, not by being loaded again.
  //
  // When Payien will not serve it — which is production's state today, for
  // want of a registered origin — nothing is said and nothing is drawn in
  // red: the inert preview holds the band, and the ordinary session frame
  // still arrives once the form is complete, exactly as before.
  const fields = useFieldsFrame();
  const frameUrl = fields.available ? fields.fields_url : checkoutUrl;
  /*
   * Whether the screen is still being assembled behind its cover.
   *
   * Lifting the cover as soon as the frame's *address* was known put a
   * finished-looking page on screen and then grew a payment block into it a
   * moment later — the buyer had already started reading, and a block
   * appearing under their eyes reads as a fault. So the cover waits for the
   * frame to say it has drawn itself.
   *
   * Only where a frame is expected at the open. On the fallback path there
   * is no frame to wait for until the form is filled, and a cover that
   * waited for one would be a cover over the form the buyer has to fill.
   */
  const waitingForFrame = fields.pending || (fields.available && !frameDrawn);

  useEffect(() => {
    if (!waitingForFrame) return undefined;
    const timer = window.setTimeout(() => {
      setFrameDrawn(true);
      if (fields.available) setFrameStalled(true);
    }, FRAME_DRAW_DEADLINE_MS);
    return () => window.clearTimeout(timer);
  }, [waitingForFrame, fields.available]);
  const frameOrigin = fields.available ? fields.origin : undefined;

  // A click that arrived before the session did. The frame is told as soon
  // as there is something to tell it, and never before: an arming message
  // naming nothing would leave the frame charging against a session that
  // does not exist.
  useEffect(() => {
    if (!sessionId) return;
    armWanted.current = false;
    framed.current?.arm(sessionId);
    setArming(false);
  }, [sessionId]);

  /**
   * Records what was bought and moves to activation.
   *
   * Frozen here because the activation screen states these back after the
   * controls that produced them are gone.
   */
  const handlePaid = () => {
    updateValues({
      checkoutSeats: seats,
      checkoutTotal: coupon?.total ?? price.total,
      checkoutCycle: billingCycle,
      checkoutTrialDays: trial ? trialDays : 0,
      checkoutLines: plans
        .filter((p) => (seatsBy[p.key] ?? 0) > 0)
        .map((p) => ({ title: p.title, seats: seatsBy[p.key] ?? 0 })),
      checkoutSupportSeats: agency ? supportSeats(sellable, seatsBy).total : null,
    });
    setPaid(true);
    setTimeout(() => setCurrentStepId('activation'), CONFIRMATION_MS);
  };

  /**
   * Answers a pay click that landed before there was anything to charge.
   *
   * The frame has the card and no session; the host has the session and no
   * card. Neither side can tell the buyer what is wrong on its own, so the
   * host asks the frame which of *its* fields are short, adds its own, and
   * marks both — then arms the frame only if there is nothing left to fix.
   */
  const handleNeedsSession = useCallback(async () => {
    setTried(true);
    setArming(true);
    const fromFrame = await (framed.current?.requestValidity() ?? Promise.resolve([]));
    setFrameInvalid(fromFrame);
    if (!complete || fromFrame.length) {
      showFieldIssuesRef.current(fromFrame);
      // Said, not left unsaid. The host knows in the first instant that its
      // own fields are short; staying quiet would cost the buyer the
      // frame's whole arming deadline to be told so.
      framed.current?.refuseArm();
      setArming(false);
      return;
    }
    armWanted.current = true;
    if (sessionId) {
      armWanted.current = false;
      framed.current?.arm(sessionId);
      setArming(false);
      return;
    }
    if (sessionError) {
      setRetryTick((n) => n + 1);
    }
  }, [complete, sessionId, sessionError]);

  // Nothing may hold the cover open past the frame's own deadline: the frame
  // hands its button back at thirty seconds, and a page still covered then
  // would be a live control under a dead curtain.
  useEffect(() => {
    if (!arming) return;
    const timer = setTimeout(() => setArming(false), ARMING_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [arming]);

  /**
   * Asks the host's own endpoint and rebuilds the session around the code.
   *
   * The slow road, taken only when the open session cannot be asked.
   *
   * @param code - The code as the buyer typed it.
   * @returns The reason it was refused, or null when it took.
   */
  const rebuildWithCoupon = async (code: string): Promise<string | null> => {
    if (!agencyId) return t('checkoutStep.coupon.error');
    try {
      const verdict = await AgenciesService.validateCoupon(
        agencyId, code, Math.round(price.total * 100), isYearly ? 'yearly' : 'monthly',
      );
      const applied = asApplied(verdict as any);
      if (!applied) return verdict?.message || t('checkoutStep.coupon.error');
      setRebuiltCoupon(applied);
      setHeldCode(applied.code);
      return null;
    } catch {
      return t('checkoutStep.coupon.error');
    }
  };

  /**
   * Puts a code to the open session, and says why when it will not take.
   *
   * The session answers first because it can take the coupon without being
   * rebuilt — the card already typed into the frame stays where it is.
   *
   * The frame is asked with a price because it may not have one: served
   * fields-only and not yet armed, it knows no plan and no total. What goes
   * across is the figure the summary is already showing the buyer, so the
   * two cannot disagree about what is being discounted (BUG-022).
   *
   * An "applied" the host cannot make sense of — nothing off, nothing left —
   * is treated as no answer and sent down the rebuild road, which prices the
   * code against a session Payien builds itself.
   *
   * @param code - The code as the buyer typed it.
   * @returns The reason it was refused, or null when it took.
   */
  const applyCoupon = async (code: string): Promise<string | null> => {
    const outcome = await (framed.current?.applyCoupon(
      code, Math.round(price.total * 100), isYearly ? 'yearly' : 'monthly',
    ) ?? Promise.resolve({ status: 'unavailable' as const }));
    const applied = outcome.status === 'applied' ? fromSession(outcome) : null;
    if (applied) {
      setSessionCoupon(applied);
      setHeldCode(applied.code);
      return null;
    }
    // A refusal is an answer: the code is bad, and asking a second service
    // the same question would only risk a second opinion on screen.
    if (outcome.status === 'refused') return t(couponErrorKey(outcome.errorCode));
    return rebuildWithCoupon(code);
  };

  /**
   * Applies a code the buyer typed, and says so in the dialog when it fails.
   *
   * @param code - The code as the buyer typed it.
   * @returns The reason it was refused, or null when it took.
   */
  const applyTypedCoupon = async (code: string): Promise<string | null> => {
    const refusal = await applyCoupon(code);
    if (refusal) setIssues({ title: t('checkoutStep.issues.couponTitle', { code }), items: [refusal] });
    return refusal;
  };

  // The re-application itself, once the price and the session have settled.
  // A refusal keeps the code: the next change to the order tries it again,
  // which is what lets a minimum the order did not reach start to apply
  // once it does.
  const applyCouponRef = useRef(applyCoupon);
  applyCouponRef.current = applyCoupon;
  useEffect(() => {
    if (!reapply) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const refusal = await applyCouponRef.current(reapply.code);
      if (cancelled || reapply.seq !== reapplySeq.current) return;
      setReapply(null);
      if (refusal) {
        setIssues({
          title: t('checkoutStep.issues.couponTitle', { code: reapply.code }),
          items: [t('checkoutStep.issues.couponKept', { reason: refusal })],
        });
      }
    }, SETTLE_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reapply]);

  const setField = (field: keyof CheckoutForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
    if (field !== 'cep') return;
    // Eight digits is the trigger; anything shorter means the customer is
    // still typing, or has just deleted part of a code that had resolved.
    if (value.replace(/\D/g, '').length === CEP_DIGITS) zip.lookup(value);
    else zip.reset();
  };

  /**
   * Starts the card-less trial, which charges nothing and opens no session.
   *
   * It still has to produce the agency record: everything after this screen
   * is addressed to one, trial or not.
   */
  const startTrial = async () => {
    setTried(true);
    if (!complete) showFieldIssuesRef.current([]);
    if (!complete || startingTrial) return;
    setStartingTrial(true);
    try {
      await openSession();
      handlePaid();
    } catch (err) {
      setSessionError(err instanceof CheckoutCouponError
        ? err.errorCode
        : (err instanceof Error ? err.message : 'unknown'));
    } finally {
      setStartingTrial(false);
    }
  };

  const busy = charging || arming || startingTrial;
  const discountPercent = plans.find((p) => (p.discountPercent ?? 0) > 0)?.discountPercent ?? '';

  return (
    <>
      {(charging || arming || waitingForFrame) && (
        <ChargingCover charging={charging} />
      )}
      <CheckoutIssuesDialog issues={issues} onClose={() => setIssues(null)} />
      <main
        className="checkout-main"
        style={{
          width: '100%', maxWidth: 1040, margin: '0 auto',
          padding: 'var(--space-3-5) var(--space-5) var(--space-10)',
          display: 'flex', flexDirection: 'column', gap: 'var(--space-3)',
          ...HOST_FIELD_TOKENS,
        }}
      >
        {/* The attempt is forgotten; the agency is not. It was written
            before the payment session was ever opened, so dropping its id
            here does not undo it -- it only hides it from the retry, which
            then creates a second one under the same name. */}
        <CheckoutHeader onBack={() => {
          clearPersistedPaymentAttempt();
          setCurrentStepId('plan');
        }} />

        {/* Not a <form>. Nothing on this page submits one: the only button
            that charges anything lives inside the payment frame, in Payien's
            own document, and a host form around it would only add an Enter
            key that posts nothing anywhere. */}
        <div
          style={{
            display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3-5)',
            /*
             * Each column at its own height.
             *
             * `stretch` matched the two cards, which looked tidy only while
             * the form was the taller of them. It is not, now: the fields
             * are two rows instead of three and the cycle control moved to
             * the summary, so on the solo flow the summary is the tall one
             * and the form card stretched to meet it -- a hundred pixels of
             * white under the submit button, which is the thing being paid
             * for. A short card that ends where its content ends says less
             * than a matched pair, and says nothing wrong.
             */
            alignItems: 'flex-start',
          }}
        >
          <FormCard>
            <CycleRow
              cycle={billingCycle}
              onCycle={setBillingCycle}
              segment={segment}
              discountPercent={discountPercent}
              disabled={busy}
              className={agency ? 'checkout-seat-label' : 'checkout-cycle-desktop-solo'}
              radiogroupClassName="checkout-cycle-radiogroup-desktop"
            />
            {agency && (
              <>
                <SeatCards
                  plans={sellable}
                  seatsBy={seatsBy}
                  cycle={billingCycle}
                  short={tried && !enoughSeats}
                  disabled={busy}
                  onSeats={(planKey_, next) => setSeatsBy((current) => ({
                    ...current, [planKey_]: Math.max(0, Math.min(next, seatRange(
                      sellable.find((p) => p.key === planKey_),
                    ).max)),
                  }))}
                />
                <SeatFloorNotice seats={seats} />
              </>
            )}
            <PayerFields
              segment={segment}
              form={form}
              onField={setField}
              zipStatus={zip.status}
              address={zip.address}
              invalid={tried ? missing : []}
              withAddress={withAddress}
              disabled={busy}
              lockedFields={lockedFields}
            />
            {lockedFields.length > 0 && (
              <Text
                component="p"
                data-testid="checkout-payer-locked-note"
                className="m-0 text-(length:--text-body-sm) text-(--text-tertiary)"
              >
                {t('checkoutStep.payer.lockedNote')}
              </Text>
            )}
            {trial !== 'nocard' && (
              <PayZone
                url={frameUrl}
                error={sessionError}
                onRetry={() => setRetryTick((n) => n + 1)}
                waiting={fields.pending}
                stalled={frameStalled}
                onReload={() => { setFrameStalled(false); setFrameReloads((n) => n + 1); }}
              >
                {FF_MOCK_PAYMENTS && complete && (
                  <MockPaymentConfirmation
                    onConfirm={handlePaid}
                    onCancel={() => setCurrentStepId('plan')}
                  />
                )}
                {!FF_MOCK_PAYMENTS && frameUrl && (
                  <CheckoutIframe
                    ref={framed}
                    checkoutUrl={frameUrl}
                    frameOrigin={frameOrigin}
                    onSuccess={handlePaid}
                    onCancel={() => {
                      clearPersistedPaymentAttempt();
                      setCurrentStepId('plan');
                    }}
                    // The reason crosses as a code and is worded here. It
                    // used to be dropped for `payment.errorCreatingCheckout`
                    // — the sentence for a session that could not be built —
                    // so a declined card was reported as an outage and the
                    // buyer was never told the card was the problem.
                    onPaymentFailed={(errorCode, changeable) => {
                      setCharging(false);
                      setArming(false);
                      setIssues({
                        title: t('checkoutStep.issues.paymentTitle'),
                        items: [paymentErrorMessage(errorCode, changeable, t)],
                      });
                    }}
                    onFieldsInvalid={(fields) => {
                      setTried(true);
                      setFrameInvalid(fields);
                      showFieldIssuesRef.current(fields);
                    }}
                    // Bumping the tick, not clearing the URL alone: nothing
                    // the session effect watches had changed, so it never
                    // re-ran and the frame sat on an expired session.
                    onExpired={() => {
                      setCheckoutUrl(null);
                      setRetryTick((n) => n + 1);
                    }}
                    // A sessionless frame outlives its sessions, so a new one
                    // must not reload it: the card inside would go with it.
                    key={fields.available ? `fields-${frameReloads}` : checkoutUrl}
                    onNeedsSession={handleNeedsSession}
                    onReady={() => { setFrameDrawn(true); setFrameStalled(false); }}
                    // The session is the authority on the discount, so what
                    // it applied is what the summary owes.
                    onCouponApplied={(applied) => setSessionCoupon(fromSession(applied))}
                    onBusyChange={setCharging}
                  />
                )}
              </PayZone>
            )}
          </FormCard>

          <CheckoutSummary
            planName={agency ? t('planChoice.agency.title') : (plan?.title ?? '')}
            segment={segment}
            cycle={billingCycle}
            onCycle={setBillingCycle}
            disabled={busy}
            price={price}
            lines={agency ? summaryLines(sellable, seatsBy, billingCycle) : undefined}
            support={agency ? supportSeats(sellable, seatsBy) : null}
            highlights={highlights}
            discountPercent={discountPercent}
            coupon={coupon}
            trial={trial}
            trialDays={trialDays}
            trialDate={trialDate(i18n.language, trialDays)}
            trialStatus={trialStatus}
            paid={paid}
            action={trial === 'nocard' ? (
              <StartTrialButton busy={startingTrial} days={trialDays} onClick={startTrial} />
            ) : null}
            couponField={(
              <CouponField
                applied={coupon}
                onApply={applyTypedCoupon}
                disabled={busy}
                pendingCode={reapply?.code ?? null}
                // Removing is not the same as forgetting: Payien persisted
                // the discount *on the session* and takes the charge from
                // there, so the one way to un-apply it is a session that
                // never had it.
                onRemove={() => {
                  if (sessionCoupon) setRetryTick((n) => n + 1);
                  setSessionCoupon(null);
                  setRebuiltCoupon(null);
                  setHeldCode(null);
                  reapplySeq.current += 1;
                  setReapply(null);
                }}
              />
            )}
          />
        </div>
      </main>
    </>
  );
};


/**
 * The band the card is typed into, wherever that band is coming from.
 *
 * Before a session exists it is a picture of the five fields Payien will
 * render, in their place and at their size, so the buyer can see what paying
 * will involve while they are still deciding — the dashed box that used to
 * stand here said only what was missing from the form above it.
 *
 * @param props - The session URL, a failure to report, and the frame itself.
 * @returns The payment band.
 */
/**
 * The space the frame will take, held open and empty.
 *
 * This used to be a copy of the card form, disabled, and the buyer read the
 * handover as the screen locking up: the copy and the frame disagreed about
 * the order of their fields, so the form appeared to rearrange itself, and
 * the frame arrived a button taller than the picture of it had been.
 *
 * Nothing stands in now. While the frame's address is still being fetched
 * the whole screen is behind the cover, so there is nobody to show a
 * placeholder to; by the time the cover lifts the frame is mounting into
 * this box. It only has to be the right size, so that it does not grow
 * under the form when the frame lands.
 *
 * @returns The reserved, empty block.
 */
const FrameSpace: React.FC<{ waiting: boolean }> = ({ waiting }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div
      data-testid="checkout-frame-space"
      className="checkout-frame-space"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'var(--space-4)', textAlign: 'center',
        border: '1px dashed var(--border-subtle)', borderRadius: 'var(--radius-md)',
      }}
    >
      {/* Behind the cover while the frame is still being asked for; once
          there is an answer and no frame, the band says when it will come
          rather than standing empty in the middle of the form. */}
      {!waiting && (
        <Text component="span" style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
          {t('checkoutStep.payment.frameAfterForm')}
        </Text>
      )}
    </div>
  );
};

/**
 * Said over a frame that never drew itself.
 *
 * A cross-origin frame that failed shows nothing at all, and an empty white
 * box in the middle of a payment form reads as the page being broken.
 *
 * @param props - What reloading the frame does.
 * @returns The notice.
 */
const FrameStalled: React.FC<{ onReload: () => void }> = ({ onReload }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div
      data-testid="checkout-frame-stalled"
      role="alert"
      style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}
    >
      <Text component="span" style={{ fontSize: 'var(--text-small)', color: 'var(--status-error-text)' }}>
        {t('checkoutStep.payment.frameStalled')}
      </Text>
      <UnstyledButton
        onClick={onReload}
        style={{
          fontSize: 'var(--text-small)', color: 'var(--seed-base)',
          textDecoration: 'underline', cursor: 'pointer', background: 'none',
        }}
      >
        {t('checkoutStep.payment.retry')}
      </UnstyledButton>
    </div>
  );
};

const PayZone: React.FC<{
  url: string | null;
  error: string | null;
  onRetry: () => void;
  waiting: boolean;
  stalled: boolean;
  onReload: () => void;
  children: React.ReactNode;
}> = ({ url, error, onRetry, waiting, stalled, onReload, children }) => {
  const { t } = useTranslation('@pages/onboarding');
  return (
    <div
      data-testid="checkout-payzone"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2-5)' }}
    >
      {stalled && <FrameStalled onReload={onReload} />}
      {url || FF_MOCK_PAYMENTS ? children : <FrameSpace waiting={waiting} />}
      {error && (
        <div
          data-testid="checkout-session-failed"
          style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
        >
          <Text
            component="span"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--status-error-text)' }}
          >
            {/* A refusal the buyer can act on says what it was. Everything
                else keeps the generic sentence: a gateway that fell over has
                no reason to offer, and inventing one would be worse than
                saying payment could not be opened (BUG-025). */}
            {error.startsWith('coupon_')
              ? t(couponErrorKey(error))
              : t('checkoutStep.payment.failed')}
          </Text>
          <UnstyledButton
            onClick={onRetry}
            style={{
              fontSize: 'var(--text-meta)', color: 'var(--seed-base)',
              textDecoration: 'underline', cursor: 'pointer', background: 'none',
            }}
          >
            {t('checkoutStep.payment.retry')}
          </UnstyledButton>
        </div>
      )}
    </div>
  );
};

/**
 * What the cover says, and when it starts saying it.
 *
 * A charge that takes twelve seconds behind one unchanging line reads as a
 * page that has stopped. Each entry is the second from which its line holds.
 */
export const COVER_PHASES = [
  { after: 0, key: 'checkoutStep.payment.charging' },
  { after: 6000, key: 'checkoutStep.payment.phaseBank' },
  { after: 14000, key: 'checkoutStep.payment.phaseAlmost' },
  { after: 20000, key: 'checkoutStep.payment.phaseSlow' },
] as const;

/**
 * Which line the cover is on, given how long it has been up.
 *
 * @param since - When the cover went up.
 * @returns The phase index.
 */
function usePhase(active: boolean): number {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    if (!active) {
      setPhase(0);
      return;
    }
    const timers = COVER_PHASES
      .map((step, index) => (step.after === 0
        ? null
        : window.setTimeout(() => setPhase(index), step.after)))
      .filter((timer): timer is number => timer !== null);
    return () => timers.forEach(window.clearTimeout);
  }, [active]);
  return phase;
}

/**
 * Covers the whole page while the card is being charged.
 *
 * ── Why it is the whole page ──────────────────────────────────────────────
 * The framed form draws its own overlay, but it only reaches the edges of
 * the frame: everything around it — the CEP, the plan, the coupon — stayed
 * live while the money was moving, and editing any of it would leave the
 * screen showing an amount that was no longer the one being charged.
 *
 * ── Why it is a modal ─────────────────────────────────────────────────────
 * It used to be a div with `role="status"`, which stops a mouse and nothing
 * else: a screen reader walked straight through it into a form that was no
 * longer safe to edit, and Tab moved focus into controls painted over. It is
 * `aria-modal` now, takes focus when it goes up, keeps it, and gives it back
 * to whatever had it when it comes down.
 *
 * ── Why it never dismisses itself ─────────────────────────────────────────
 * The text escalates and the last phase says the wait is unusual, but the
 * cover stays: the one thing worse than a long wait is handing the buyer a
 * live form back while their card is still being charged. The stretch that
 * *can* end on its own is the arming one, and its deadline lives with the
 * message that started it.
 */
export const ChargingCover: React.FC<{ charging: boolean }> = ({ charging }) => {
  const { t } = useTranslation('@pages/onboarding');
  const phase = usePhase(charging);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    box.current?.focus();
    return () => previous?.focus?.();
  }, []);

  return (
    <div
      ref={box}
      tabIndex={-1}
      data-testid="checkout-charging-cover"
      role="dialog"
      aria-modal="true"
      // Named by the line it shows: a modal without an accessible name is
      // announced as "dialog" and nothing else, which on this screen is the
      // one moment the buyer most needs told what is happening.
      aria-labelledby="checkout-cover-label"
      aria-live="polite"
      // Tab is swallowed rather than cycled: there is nothing in here to
      // cycle between, and the only alternative is letting focus land on a
      // control the buyer cannot see and must not use.
      onKeyDown={(event) => { if (event.key === 'Tab') event.preventDefault(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, outline: 'none',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 'var(--space-4)',
        background: charging
          ? 'color-mix(in srgb, var(--surface-primary) 92%, transparent)'
          : 'var(--surface-primary)',
        backdropFilter: charging ? 'blur(2px)' : 'none',
      }}
    >
      <LogoRingSpinner />
      <Text
        component="span"
        id="checkout-cover-label"
        style={{
          fontSize: 'var(--text-body)', color: 'var(--text-secondary)',
          textAlign: 'center', maxWidth: 420, padding: '0 var(--space-5)',
        }}
      >
        {t(charging ? COVER_PHASES[phase].key : 'checkoutStep.payment.preparing')}
      </Text>
    </div>
  );
};

export default Checkout;
